<script lang="ts" setup>
import {
    faTimes,
    faMobile,
    faTablet,
    faDesktop,
    faRefresh,
    faCopy,
    faCheck,
    faLanguage,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { LanguageSelectorProps } from "./LanguageSelector.vue";
import type { Locale } from "~/models/Localization";
type ViewMode = "mobile" | "tablet" | "desktop";

export type HidePreviewModalMetadata = boolean | undefined;

interface PreviewModalProps {
    name: string;
    src: string;
    copySrc?: string;
    languageProps?: LanguageSelectorProps;
    defaultView?: ViewMode;
    defaultLocale?: Locale | { id?: number | null } | null;
    defaultLocaleCode?: string;
    hideMetadata?: boolean;
}

const props = defineProps<PreviewModalProps>();

const show = defineModel<boolean>("show");
const view = defineModel<ViewMode>("view", {
    default: null,
});

onBeforeMount(() => {
    if (!view.value) {
        view.value = props.defaultView ?? "desktop";
    }
});

const loaded = ref<boolean>(false);
const refresh = ref<number>(Date.now());

watch(refresh, () => {
    loaded.value = false;
});

const theme = computed(() => {
    if (view.value === "mobile") {
        return {
            frame: "min-h-[60vh] max-h-[60vh]",
            modal: {
                width: "w-full sm:max-w-lg !opacity-100",
            },
        };
    }

    if (view.value === "tablet") {
        return {
            frame: "min-h-[75vh] max-h-[75vh]",
            modal: {
                width: "w-full sm:max-w-4xl !opacity-100",
            },
        };
    }

    return {
        frame: "min-h-full",
        card: {
            base: "flex flex-col h-full",
            body: {
                base: "flex-1",
            },
        },
        modal: {
            width: "w-full sm:max-w-screen !opacity-100",
            height: "h-screen sm:max-h-screen",
            margin: "sm:my-0",
        },
    };
});

const locale = defineModel<Locale | { id?: number | null } | null>("locale", {
    default: null,
});

const src = computed(() => {
    if (locale.value?.id) {
        const src = new URL(
            props.src.startsWith("/")
                ? `${location.origin}${props.src}`
                : props.src,
        );
        src.searchParams.set("locale_id", locale.value.id.toString());
        return src.toString();
    }

    return props.src;
});

const copySrc = computed(() => {
    if (locale.value?.id) {
        const copySrc = props.copySrc ?? props.src;
        const src = new URL(
            copySrc.startsWith("/") ? `${location.origin}${copySrc}` : copySrc,
        );
        src.pathname = `${src.pathname}/${locale.value.code}`;
        return src.toString();
    }

    return props.copySrc ?? props.src;
});

watch(locale, () => {
    refresh.value = Date.now();
});

watch(refresh, () => {
    metadata.value = null;
    previewId.value = null;
});

const previewId = ref<string | null>(null);
const metadata = ref<Record<string, any> | null>(null);

const hidePreviewModalMetadata = inject<HidePreviewModalMetadata>(
    "hidePreviewModalMetadata",
    undefined,
);
const hideMetadata = computed(() => {
    return !hidePreviewModalMetadata ? metadata.value : false;
});

const handleMessage = (event: MessageEvent) => {
    if (
        typeof event.data === "object" &&
        event.data.type === "previewMetadata"
    ) {
        metadata.value = event.data.metadata;
        previewId.value = event.data.previewId;
    }
};

onBeforeMount(() => {
    window.addEventListener("message", handleMessage);
});

onBeforeUnmount(() => {
    window.removeEventListener("message", handleMessage);
});

const iframe = ref<HTMLIFrameElement | null>(null);

function onHighLightLinkMouseEnter(url: string) {
    if (iframe.value) {
        iframe.value.contentWindow?.postMessage(
            {
                previewId: previewId.value,
                anchorMouseEnter: url,
            },
            "*",
        );
    }
}

function onHighLightLinkMouseLeave(url: string) {
    if (iframe.value) {
        iframe.value.contentWindow?.postMessage(
            {
                previewId: previewId.value,
                anchorMouseLeave: url,
            },
            "*",
        );
    }
}

function getQueryParams(url: string) {
    const urlObj = new URL(url);
    const params = Object.fromEntries(urlObj.searchParams.entries());

    return Object.entries(params).map(([key, value]) => ({
        parameter: key,
        value,
    }));
}
</script>
<template>
    <UModal v-model="show" :ui="theme.modal" @after-leave="loaded = false">
        <UCard
            :ui="{
                ...(theme.card ?? {}),
                header: {
                    ...(theme.card?.header ?? {}),
                    base: 'grid items-center grid-cols-3',
                },
            }"
        >
            <template #header>
                <span
                    class="text-lg font-medium font-display"
                    v-if="view === 'desktop'"
                    >{{ name }}</span
                >
                <span v-else />
                <div class="flex justify-center items-center gap-4">
                    <UICopy v-slot="{ copy, copied }" :source="copySrc">
                        <UTooltip text="Copy Preview URL">
                            <UButton
                                class="!scale-100"
                                square
                                color="white"
                                size="lg"
                                @click="copy(copySrc)"
                            >
                                <FontAwesomeIcon
                                    :icon="copied ? faCheck : faCopy"
                                    fixed-width
                                />
                            </UButton>
                        </UTooltip>
                    </UICopy>
                    <UButtonGroup size="lg">
                        <UButton
                            class="!scale-100"
                            square
                            :color="view === 'mobile' ? 'gray' : 'white'"
                            @click="view = 'mobile'"
                        >
                            <FontAwesomeIcon :icon="faMobile" fixed-width />
                        </UButton>
                        <UButton
                            class="!scale-100"
                            square
                            :color="view === 'tablet' ? 'gray' : 'white'"
                            @click="view = 'tablet'"
                        >
                            <FontAwesomeIcon :icon="faTablet" fixed-width />
                        </UButton>
                        <UButton
                            class="!scale-100"
                            square
                            :color="view === 'desktop' ? 'gray' : 'white'"
                            @click="view = 'desktop'"
                        >
                            <FontAwesomeIcon :icon="faDesktop" fixed-width />
                        </UButton>
                    </UButtonGroup>
                    <UButton
                        class="!scale-100 h-9 w-9 justify-center"
                        square
                        color="white"
                        @click="refresh = Date.now()"
                    >
                        <FontAwesomeIcon :icon="faRefresh" fixed-width />
                    </UButton>
                </div>
                <div class="flex items-center justify-end gap-4">
                    <AppLanguageSelector
                        v-if="languageProps && view !== 'mobile'"
                        v-model="locale"
                        v-bind="languageProps"
                        compact
                        :none-label="defaultLocale?.name ?? 'Default Language'"
                        with-icon
                        class="w-64 flex-shrink-0"
                    />
                    <UButton
                        square
                        size="md"
                        color="gray"
                        variant="ghost"
                        class="w-9 h-9"
                        @click="show = false"
                    >
                        <FontAwesomeIcon
                            :icon="faTimes"
                            fixed-width
                            size="xl"
                            class="text-gray-600"
                        />
                    </UButton>
                </div>
                <div
                    v-if="view === 'mobile'"
                    class="mt-4 flex items-center justify-center col-span-3"
                >
                    <AppLanguageSelector
                        v-if="languageProps"
                        v-model="locale"
                        v-bind="languageProps"
                        compact
                        :none-label="defaultLocale?.name ?? 'Default Language'"
                        with-icon
                        class="w-64"
                        :default-locale-code="defaultLocaleCode"
                    />
                </div>
            </template>
            <div
                class="relative w-full flex gap-4 h-full border border-gray-200 rounded-xl overflow-hidden"
            >
                <iframe
                    ref="iframe"
                    :key="refresh"
                    :src="src"
                    class="h-full flex-1"
                    :class="theme.frame"
                    @load="loaded = true"
                />
                <div
                    v-if="hideMetadata"
                    class="bg-white flex flex-col gap-4 py-4 pr-4"
                >
                    <UIDataList
                        v-if="metadata.links?.length"
                        condensed
                        small
                        class="bg-hd-neutral-50 w-80 rounded-md pb-2.5"
                        item-padding="py-1"
                        :items="metadata.links"
                        grid="grid grid-cols-2 gap-2"
                    >
                        <template #title>Links</template>
                        <template #description>
                            <div class="text-sm text-gray-500">
                                Hover over a link to highlight it in the preview
                                and see the full URL.
                            </div>
                        </template>
                        <template #value="{ item }">
                            <HdPopoverTip
                                :popper="{
                                    placement: 'left',
                                }"
                            >
                                <template #default>
                                    <div
                                        class="text-sm w-96 p-2 text-pretty flex flex-col gap-2"
                                    >
                                        <div
                                            class="border-l-4 border-gray-200 p-2 rounded-md bg-gray-50 break-all"
                                        >
                                            {{ item.href }}
                                        </div>
                                        <UTable
                                            class="border border-gray-200 bg-gray-50 rounded-md"
                                            :ui="{
                                                th: {
                                                    padding: 'px-2 py-1.5',
                                                },
                                                td: {
                                                    padding: 'px-2 py-1.5',
                                                },
                                            }"
                                            v-if="
                                                getQueryParams(item.href).length
                                            "
                                            :rows="getQueryParams(item.href)"
                                        />
                                    </div>
                                </template>
                                <template #trigger>
                                    <div class="flex gap-4 w-full">
                                        <span
                                            class="text-sm inline-block w-3"
                                            >{{ item.index }}</span
                                        >
                                        <UButton
                                            :padded="false"
                                            variant="link"
                                            @mouseenter="
                                                onHighLightLinkMouseEnter(
                                                    item.href,
                                                )
                                            "
                                            @mouseleave="
                                                onHighLightLinkMouseLeave(
                                                    item.href,
                                                )
                                            "
                                            class="w-64"
                                        >
                                            <span class="truncate">{{
                                                item.text || item.href
                                            }}</span>
                                        </UButton>
                                    </div>
                                </template>
                            </HdPopoverTip>
                        </template>
                    </UIDataList>
                    <UIDataList
                        v-if="metadata.utm_campaign"
                        condensed
                        class="bg-hd-neutral-50 w-80 rounded-md pb-2.5"
                        item-padding="py-1"
                        :items="metadata.utm_campaign"
                    >
                        <template #title>UTM Campaign</template>
                        <template #label="{ index }">
                            {{ $t(`mail.blocks.labels.${index}`) }}
                        </template>
                        <template #value="{ item }">
                            <span>{{ item }}</span>
                        </template>
                    </UIDataList>
                </div>
                <HdLoader
                    :loading="!loaded"
                    class="top-0 left-0 absolute z-10 w-full h-full bg-white"
                />
            </div>
            <template #footer v-if="$slots.footer">
                <slot name="footer" />
            </template>
        </UCard>
    </UModal>
</template>
